import React, { useEffect, useState } from 'react'
import { Table, Typography, Button, Modal, Form, Input, Select, message, Popconfirm, Tag, Empty } from 'antd'
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { routinesAPI, clientsAPI } from '../services/api'

const { Title, Text } = Typography

const dayLabels = [
  { value: 'lunes', label: 'Lunes' },
  { value: 'martes', label: 'Martes' },
  { value: 'miercoles', label: 'Miércoles' },
  { value: 'jueves', label: 'Jueves' },
  { value: 'viernes', label: 'Viernes' },
  { value: 'sabado', label: 'Sábado' },
  { value: 'domingo', label: 'Domingo' }
]

function Rutinas() {
  const [routines, setRoutines] = useState([])
  const [clients, setClients] = useState([])
  const [modalVisible, setModalVisible] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form] = Form.useForm()

  useEffect(() => { load(); loadClients() }, [])

  const load = async () => {
    try {
      const res = await routinesAPI.getAll()
      setRoutines(res.data)
    } catch (e) { message.error('Error cargando rutinas') }
  }

  const loadClients = async () => {
    try {
      const res = await clientsAPI.getAll()
      setClients(res.data)
    } catch (e) {}
  }

  const openModal = (routine) => {
    setEditing(routine || null)
    if (routine) {
      form.setFieldsValue({
        name: routine.name,
        description: routine.description,
        days: routine.days || [],
        exercises: Array.isArray(routine.exercises) ? routine.exercises.join('\n') : '',
        clientId: routine.clientId || undefined
      })
    } else {
      form.resetFields()
    }
    setModalVisible(true)
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      const data = {
        name: values.name,
        description: values.description,
        days: values.days || [],
        exercises: values.exercises ? values.exercises.split('\n').map(s => s.trim()).filter(Boolean) : [],
        clientId: values.clientId || null
      }
      if (editing) {
        await routinesAPI.update(editing.id, data)
        message.success('Rutina actualizada')
      } else {
        await routinesAPI.create(data)
        message.success('Rutina creada')
      }
      setModalVisible(false)
      load()
    } catch (e) {
      message.error('Error guardando rutina')
    }
  }

  const handleDelete = async (id) => {
    try {
      await routinesAPI.remove(id)
      message.success('Rutina eliminada')
      load()
    } catch (e) { message.error('Error') }
  }

  const columns = [
    { title: 'Rutina', dataIndex: 'name', render: (v) => <Text strong>{v}</Text> },
    { title: 'Asignada a', dataIndex: 'Client', ellipsis: true, responsive: ['md'], render: (c) => c ? `${c.name} ${c.lastName}` : <Tag>General</Tag> },
    { title: 'Días', dataIndex: 'days', responsive: ['lg'], render: (d) => (d || []).map(x => <Tag key={x} style={{ marginBottom: 2 }}>{dayLabels.find(dl => dl.value === x)?.label || x}</Tag>) },
    { title: 'Acciones', key: 'actions', width: 90, render: (_, r) => (
      <span>
        <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openModal(r)} />
        <Popconfirm title="¿Eliminar rutina?" onConfirm={() => handleDelete(r.id)}>
          <Button size="small" type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </span>
    )}
  ]

  return (
    <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <Title level={4} style={{ margin: 0 }}>Rutinas</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal(null)}>
          Nueva Rutina
        </Button>
      </div>
      <Text style={{ display: 'block', marginBottom: 12, color: '#999' }}>
        Armá las rutinas y asignalas a una alumna. Las rutinas "General" las ven todas las alumnas.
      </Text>

      <div style={{ overflowX: 'auto' }}>
        <Table
          dataSource={routines}
          columns={columns}
          rowKey="id"
          size="small"
          scroll={{ x: 500 }}
          pagination={{ pageSize: 15, size: 'small' }}
          locale={{ emptyText: <Empty description="Todavía no hay rutinas" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          expandable={{
            expandedRowRender: (r) => (
              <div>
                {r.description && <Text style={{ color: '#ccc' }}>{r.description}</Text>}
                <div style={{ marginTop: 6 }}>
                  {(r.exercises || []).length > 0 ? (
                    <ol style={{ margin: 0, paddingLeft: 20 }}>
                      {(r.exercises || []).map((e, i) => <li key={i}><Text style={{ color: '#ccc' }}>{e}</Text></li>)}
                    </ol>
                  ) : <Text style={{ color: '#666' }}>Sin ejercicios cargados</Text>}
                </div>
              </div>
            )
          }}
        />
      </div>

      <Modal title={editing ? 'Editar Rutina' : 'Nueva Rutina'} open={modalVisible} onOk={handleSubmit} onCancel={() => setModalVisible(false)} okText={editing ? 'Guardar' : 'Crear'} width={520}>
        <Form form={form} layout="vertical" size="small">
          <Form.Item name="name" label="Nombre" rules={[{ required: true, message: 'Nombre de la rutina' }]}>
            <Input placeholder="Ej: Full Body Martes" />
          </Form.Item>
          <Form.Item name="description" label="Descripción">
            <Input.TextArea rows={2} placeholder="Descripción breve..." />
          </Form.Item>
          <Form.Item name="days" label="Días de la semana">
            <Select mode="multiple" placeholder="Días en que se entrena" options={dayLabels} allowClear />
          </Form.Item>
          <Form.Item name="clientId" label="Asignar a (opcional)">
            <Select
              allowClear
              showSearch
              placeholder="General (todas)"
              optionFilterProp="children"
              filterOption={(input, option) => (option.children || '').toString().toLowerCase().includes(input.toLowerCase())}
            >
              {clients.map(c => <Select.Option key={c.id} value={c.id}>{c.name} {c.lastName}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="exercises" label="Ejercicios" tooltip={<span>Uno por línea</span>}>
            <Input.TextArea rows={6} placeholder={"Press banca 4x10\nSentadilla 3x12\n..."} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Rutinas
